/**
 * @file ExtractedInformationSkeleton.tsx
 *
 * @description This component renders a loading placeholder for the extracted information
 * section of the police records interface. It displays a skeleton UI that mirrors the
 * structure of the real ExtractedInformation component so that no layout shift occurs
 * when the actual form content arrives.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import { Box, Paper, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * @function ExtractedInformationSkeleton
 *
 * @description This component renders a placeholder loading state for extracted case
 * information. Its footprint matches the real ExtractedInformation component: a
 * `Paper elevation={2}` wrapper with fixed `p: 3`, an optional title heading at the
 * top, and a stack of section/field placeholders. Width is controlled by the parent
 * `Container maxWidth="xl"`, so the skeleton does not impose its own max width.
 *
 * Accessibility:
 * - Skeletons are recognized by screen readers as loading states
 *
 * @returns {JSX.Element} A skeleton UI component mirroring the extracted information layout
 */

export const ExtractedInformationSkeleton = () => {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        backgroundColor: "background.paper",
      }}
    >
      {/* Title placeholder ("Report Details" heading) */}
      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 3 }} />

      <Box sx={{ width: "100%" }}>
        {/* Stacked field groups mirroring the read-only form fields */}
        <Grid container spacing={3} flexDirection="column">
          <Grid size={{ xs: 12 }}>
            <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={56} width="100%" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={56} width="100%" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={120} width="100%" />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};
